// Phase JB.3b — Referral card.
//
// Auth empty: "Sign in to get your referral code."
// Auth populated: code + share URL preview + this user's own stats +
// reward state. N06: ONLY the user's own real numbers — never a
// platform-wide "1,247 invites sent" figure.

import type { CSSProperties } from "react";
import { Badge, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useReferral } from "../../conversion/useReferral.ts";

const fontBody = "var(--sa-font-body)";
const fontMono = "var(--sa-font-mono)";

const wrapperStyle: CSSProperties = { padding: "12px 48px 24px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

export interface ReferralCardProps {
  /** Test override. */
  forceAnon?: boolean;
}

export function ReferralCard({ forceAnon }: ReferralCardProps = {}): JSX.Element {
  const { user } = useAuthSession();
  const { code, shareUrl, stats, loading, copy, share } = useReferral();
  const isAnon = forceAnon === true || !user;

  if (isAnon) {
    return (
      <section aria-label="Referrals" data-testid="referral-card-anon" style={wrapperStyle}>
        <div style={cardStyle}>
          <Eyebrow>Referrals · Invite a friend</Eyebrow>
          <p
            style={{
              fontFamily: fontBody,
              fontSize: 14,
              color: "var(--sa-fg-secondary)",
              marginTop: 14,
              lineHeight: 1.7,
            }}
          >
            Sign in to get your referral code. STRIKZA only credits you when the friend you sent actually subscribes — never on a click, never on a sign-up alone.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section aria-label="Referrals" data-testid="referral-card-loading" style={wrapperStyle}>
        <div style={cardStyle}>
          <span style={{ fontFamily: fontMono, fontSize: 10.5, letterSpacing: "0.22em", color: "var(--sa-fg-muted)" }}>
            LOADING YOUR REFERRAL CODE…
          </span>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Referrals" data-testid="referral-card-populated" style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Referrals · Your code</Eyebrow>
          <Pill>YOU</Pill>
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <span
            data-testid="referral-card-code"
            style={{
              fontFamily: fontMono,
              fontSize: 28,
              letterSpacing: "0.18em",
              color: "var(--sa-fg-primary)",
              fontWeight: 600,
            }}
          >
            {code ?? "—"}
          </span>
          <Badge variant="ghost">Reward: 1 free month on first conversion</Badge>
        </div>

        {shareUrl && (
          <div
            data-testid="referral-card-shareurl"
            style={{
              marginTop: 12,
              fontFamily: fontMono,
              fontSize: 12,
              color: "var(--sa-fg-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {shareUrl}
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <button
            data-testid="referral-card-copy"
            type="button"
            onClick={() => void copy()}
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: "0.18em",
              padding: "8px 14px",
              borderRadius: 4,
              border: "1px solid var(--sa-hairline)",
              background: "transparent",
              color: "var(--sa-fg-primary)",
              cursor: "pointer",
            }}
          >
            COPY LINK
          </button>
          <button
            data-testid="referral-card-share"
            type="button"
            onClick={() => void share()}
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: "0.18em",
              padding: "8px 14px",
              borderRadius: 4,
              border: "1px solid var(--sa-accent-saffron)",
              background: "var(--sa-accent-saffron)",
              color: "var(--sa-bg-base)",
              cursor: "pointer",
            }}
          >
            SHARE
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <HairlineRow first columns="1fr 1fr 1fr">
            <Stat label="You sent" value={String(stats.sent)} />
            <Stat label="Signed up" value={String(stats.signedUp)} />
            <Stat label="Converted" value={String(stats.converted)} accent />
          </HairlineRow>
        </div>

        <p
          style={{
            marginTop: 14,
            fontFamily: fontBody,
            fontSize: 12.5,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.6,
          }}
        >
          These are your own numbers — not the platform's. STRIKZA never inflates a referral page with company-wide stats.
        </p>
      </div>
    </section>
  );
}
